import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Avatar, Box, Chip, Stack, IconButton, TextField, Typography } from "@material-ui/core";
import { Close } from "@material-ui/icons";
import { ListingDialogInputFee } from "../form/ListingDialogInputFee";
import { CustomNumericFormat } from "./CustomNumericFormat";
import { calculateFee, calculateFinalFee } from "utils/listings/new";
import type { AdditionalUserWithFee } from "../../../../../@types/listings/user";
import type { ChangeEvent } from "react";
import type { BuyerSellerSideProps } from "./BuyerSellerSide";

type BuyerSellerFeeProps = Pick<
  BuyerSellerSideProps,
  "side" | "loading" | "personsFeeData" | "organizationUsers" | "onPersonsFeeDataChange"
> & {
  index: number;
  personFeeData: AdditionalUserWithFee;
  finalListingPrice: number;
};

export function BuyerSellerFee({
  side,
  index,
  loading,
  personFeeData,
  personsFeeData,
  finalListingPrice,
  organizationUsers,
  onPersonsFeeDataChange
}: BuyerSellerFeeProps): JSX.Element {
  const { t: translate } = useTranslation();

  const {
    name,
    email,
    avatarUrl,
    fee = null,
    finalFee = null,
    feeIncludesTaxes = false
  } = personFeeData;

  useEffect(() => {
    if (!finalListingPrice) return;

    const newFee = calculateFee(finalFee, finalListingPrice, feeIncludesTaxes);

    const newPersonFeeData: AdditionalUserWithFee[] = [
      ...personsFeeData.slice(0, index),
      { ...personFeeData, fee: newFee },
      ...personsFeeData.slice(index + 1)
    ];

    onPersonsFeeDataChange(newPersonFeeData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finalFee, feeIncludesTaxes, finalListingPrice]);

  useEffect(() => {
    if (!finalListingPrice) return;

    const newPersonFeeData: AdditionalUserWithFee[] = personsFeeData.map((owner) => {
      const newFinalFee = calculateFinalFee(owner.fee, finalListingPrice, owner.feeIncludesTaxes);
      return { ...owner, finalFee: newFinalFee };
    });

    onPersonsFeeDataChange(newPersonFeeData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fee, feeIncludesTaxes, finalListingPrice]);

  const handleOwnersDataChange =
    (name: Extract<keyof AdditionalUserWithFee, "fee" | "finalFee" | "feeIncludesTaxes">) =>
    ({ target: { value, checked } }: ChangeEvent<HTMLInputElement>): void => {
      const currentBuyerOrigin = personsFeeData[index];

      const newValue = ["fee", "finalFee"].includes(name) ? value : checked;

      const newBuyerOrigin: AdditionalUserWithFee = {
        ...currentBuyerOrigin,
        [name]: newValue ?? null
      };

      const newPersonFeeData: AdditionalUserWithFee[] = [
        ...personsFeeData.slice(0, index),
        newBuyerOrigin,
        ...personsFeeData.slice(index + 1)
      ];

      onPersonsFeeDataChange(newPersonFeeData);
    };

  const handleRemoveOwner = (): void => {
    const newPersonFeeData: AdditionalUserWithFee[] = personsFeeData.filter(
      ({ email: userEmail }) => userEmail !== email
    );

    onPersonsFeeDataChange(newPersonFeeData);
  };

  const removePersonDisabled = side === "seller" && personsFeeData.length === 1;

  const isPersonFromOrganization = organizationUsers.some(
    ({ email: userEmail }) => userEmail === email
  );

  return (
    <Stack
      sx={{ p: 2, border: 1, borderRadius: 1, borderColor: "divider" }}
      gap={2}
      direction="row"
      alignItems="center"
    >
      <Stack gap={2} direction="row" flexWrap="wrap">
        <Box sx={{ width: { xs: "100%", sm: 320 } }}>
          <Stack direction="row" justifyContent="start" alignItems="center" spacing={2}>
            <Avatar sx={{ width: 48, height: 48 }} src={avatarUrl} />
            <Stack spacing={0.5}>
              <Typography variant="subtitle1">{name ?? email}</Typography>
              {!isPersonFromOrganization && (
                <Chip label={translate("listingsPage.external")} color="error" size="small" />
              )}
            </Stack>
          </Stack>
        </Box>
        <Box sx={{ width: { xs: "100%", sm: 192 } }}>
          <ListingDialogInputFee
            fee={fee}
            loading={loading}
            required={isPersonFromOrganization}
            feeIncludesTaxes={feeIncludesTaxes}
            handleChange={handleOwnersDataChange("fee")}
            handleChangeChecked={handleOwnersDataChange("feeIncludesTaxes")}
          />
        </Box>
        <Box sx={{ width: { xs: "100%", sm: 192 } }}>
          <TextField
            fullWidth
            value={finalFee ?? ""}
            required={isPersonFromOrganization}
            label={translate(`listingsPage.tableColumns.${side}Fee`)}
            disabled={loading}
            InputProps={{
              inputComponent: CustomNumericFormat as never
            }}
            onChange={handleOwnersDataChange("finalFee")}
          />
        </Box>
      </Stack>
      <IconButton disabled={removePersonDisabled || loading} onClick={handleRemoveOwner}>
        <Close />
      </IconButton>
    </Stack>
  );
}
